// Die Registerleiste des Kontaktdetails (ST-ACT-001, Slice 4b; NFR-UX-004,
// NFR-ACC-001).
//
// EINE LISTE STATT EINES UMBAUS: ein weiteres Register (Vertrieb, Presse,
// Lektorat) ist ein Eintrag in KONTAKT_REGISTER, mehr nicht. `kontakt.tsx`
// kennt kein einzelnes Register -- es reicht den Kontext durch und merkt sich
// die Kennung des gewaehlten. Ein Register ohne Inhalt waere eine Attrappe
// (ADR-0002), darum stehen heute genau die beiden mit Inhalt darin.
//
// ALLE Panels bleiben eingehaengt, die nicht gewaehlten sind `hidden`. Ein
// Registerwechsel darf nichts verwerfen (NFR-UX-005, CM-UX-004): angefangene
// Namen, das Wiedervorlage-Formular und eine laufende Speicherung leben in den
// Panels und waeren sonst weg. Gemessen: mit Aushaengen ging der Wert im
// Nachnamenfeld und das Datum der Wiedervorlage verloren (Codex-Cloud-Review #380).
// Mehrere Register duerfen EIN Panel teilen (`Panel` ist dieselbe Komponente):
// es wird einmal eingehaengt und bekommt `registerId`, um sich anzupassen. So
// gibt es den Verlauf genau einmal, mit einer Kennung, einer Statusregion und
// einem Abruf.

import { useCallback, useEffect, useRef, useState, type ComponentType, type KeyboardEvent, type ReactElement, type RefObject } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Caption1,
  Card,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

import { fetchParty, formatInstant, type PartyDetail } from '../../parties.ts';
import { careLinks, verlaufBezeichnung, type CareLink, type FollowUpTally } from '../../party-360.ts';
import { notWritableNotice } from '../../party-lifecycle.ts';
import { ABSCHNITT_SCHRIFT } from '../../shell/masse.ts';
import { AppLink } from '../../shell/router.tsx';
import { useScreenStyles } from '../../shell/screen-styles.ts';
import { Adressen } from './adressen-abschnitt.tsx';
import { Beziehungen } from './beziehungen-abschnitt.tsx';
import { Kontaktwege } from './kontaktwege-abschnitt.tsx';
import { Sperren, sperrenFuer, useSperren, type SperrenFuer } from './sperren-abschnitt.tsx';
import { useUeberOrganisation } from './ueber-organisation.tsx';
import { NameAbschnitt } from './name-abschnitt.tsx';
import { useKontaktStyles } from './stile.ts';
import { Stichworte } from './stichworte.tsx';
import { Verlauf } from './verlauf.tsx';
import { panelId, tabId } from './ids.ts';
import { Zugehoerigkeit } from './zugehoerigkeit.tsx';

/** Akkordeonkoepfe tragen dieselbe Schrift wie die uebrigen Abschnittsueberschriften (CM-UX-001). */
const useKopfStyles = makeStyles({
  kopf: { fontSize: ABSCHNITT_SCHRIFT, lineHeight: tokens.lineHeightBase300, margin: 0 },
  knopf: { fontSize: ABSCHNITT_SCHRIFT, lineHeight: tokens.lineHeightBase300, fontWeight: tokens.fontWeightSemibold },
});

function Akkordeonkopf({ id, children }: { readonly id?: string; readonly children: string }): ReactElement {
  const kopf = useKopfStyles();
  return (
    <AccordionHeader as="h2" {...(id === undefined ? {} : { id })} className={kopf.kopf} button={{ className: kopf.knopf }}>
      {children}
    </AccordionHeader>
  );
}

/** Alles, was ein Panel vom Bildschirm braucht; die Panels holen sich sonst nichts. */
export interface KontaktKontext {
  readonly party: PartyDetail;
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  readonly accountId: string | undefined;
  readonly onParty: (party: PartyDetail) => void;
  readonly onStand: (tally: FollowUpTally | undefined) => void;
  /** Die Sperrlage für den Kopf (Nachtrag DESIGN 19.10. V5): aus demselben Abruf wie "Sperren", kein zweiter. */
  readonly onSperren?: (partyId: string, gesperrt: SperrenFuer | undefined) => void;
  /** Die offenen Abschnitte des Accordions im Ueberblick; der Bildschirm fuehrt sie. */
  readonly offen: readonly string[];
  readonly onOffen: (offen: readonly string[]) => void;
  readonly nameRef: RefObject<HTMLDivElement | null>;
}

export interface PanelProps {
  readonly kontext: KontaktKontext;
  /** Das gewaehlte Register, auch wenn dieses Panel gerade ausgeblendet ist. */
  readonly registerId: string;
  /** Wechselt das Register (etwa "Ganzen Verlauf anzeigen" im Ueberblick). */
  readonly onRegister?: (id: string) => void;
}

export interface KontaktRegister {
  readonly id: string;
  readonly label: string;
  /** Fehlt es, steht das Register jedem Kontakt zur Verfuegung. */
  readonly angeboten?: (party: PartyDetail) => boolean;
  /** Zeigt das Register den Verlauf mit dem Notizfeld? Darauf fuehrt "Notiz schreiben". */
  readonly zeigtVerlauf: boolean;
  readonly Panel: ComponentType<PanelProps>;
}

function mitAccount(accountId: string | undefined): { readonly accountId?: string } {
  return accountId === undefined ? {} : { accountId };
}

/**
 * Der Server lehnte ein Schreiben mit 409 party_not_writable ab: der Kontakt wird neu gelesen, damit die
 * Leiste und die ausgeblendeten Wege den Zustand zeigen. Ohne Skelett und ohne das Register
 * zurueckzusetzen (kontakt.tsx laedt dafuer nicht neu); ein Lesefehler aendert nichts.
 */
function useKontaktAktualisieren(kontext: KontaktKontext): () => void {
  const { apiBaseUrl, accessToken, onParty, party } = kontext;
  const partyId = party.partyId;
  // Wechselt die Seite waehrend des Abrufs zu einem anderen Kontakt oder verlaesst sie den Kontakt,
  // gilt die verspaetete Antwort nicht mehr.
  const aktuell = useRef<string | undefined>(partyId);
  useEffect(() => {
    aktuell.current = partyId;
    return () => {
      aktuell.current = undefined;
    };
  }, [partyId]);
  return useCallback(() => {
    void (async () => {
      try {
        const token = await accessToken();
        if (token === undefined) return;
        const ergebnis = await fetchParty({ baseUrl: apiBaseUrl, accessToken: token, partyId });
        if (ergebnis.outcome === 'loaded' && ergebnis.party.partyId === aktuell.current) onParty(ergebnis.party);
      } catch {
        // Der Satz der Ablehnung steht schon im Abschnitt; ohne neuen Stand bleibt alles wie es war.
      }
    })();
  }, [accessToken, apiBaseUrl, onParty, partyId]);
}

function VerlaufInhalt({ kontext, registerId, onRegister }: { readonly kontext: KontaktKontext } & Pick<PanelProps, 'registerId' | 'onRegister'>): ReactElement {
  const aktualisieren = useKontaktAktualisieren(kontext);
  // Im Ueberblick die neuesten Eintraege; der ganze Verlauf steht EINMAL, im
  // Register "Verlauf" (M-12, Michael D-08). Dasselbe Panel, derselbe Abruf.
  const kompakt = registerId !== 'verlauf';
  return (
    <Verlauf
      kompakt={kompakt}
      {...(kompakt && onRegister !== undefined ? { onGanzerVerlauf: () => onRegister('verlauf') } : {})}
      apiBaseUrl={kontext.apiBaseUrl}
      accessToken={kontext.accessToken}
      onReauthenticate={kontext.onReauthenticate}
      partyId={kontext.party.partyId}
      onStand={kontext.onStand}
      nurLesen={notWritableNotice(kontext.party.lifecycleStatus) !== undefined}
      onNichtBeschreibbar={aktualisieren}
      {...mitAccount(kontext.accountId)}
    />
  );
}

/**
 * Der Weg zur Pflegeseite am Ende einer Abschnittsueberschrift (v4 h2 .end).
 * Sichtbar kurz "Pflegen"; der Name sagt, was gepflegt wird, und enthaelt das
 * sichtbare Wort (WCAG 2.5.3). An einem nicht beschreibbaren Kontakt steht der
 * Name der Seite selbst da, ohne "pflegen".
 */
function Pflegeweg({ weg }: { readonly weg: CareLink | undefined }): ReactElement | null {
  const own = useKontaktStyles();
  if (weg === undefined) return null;
  const pflegen = weg.label.endsWith(' pflegen');
  if (!pflegen) {
    return (
      <AppLink to={weg.href} className={own.pflegeweg}>
        {weg.label}
      </AppLink>
    );
  }
  // Der Name entsteht aus dem Text: "Kontaktwege " nur vorgelesen, sichtbar "pflegen"
  // mit grossem Anfang. So heisst der Link wie seine Seite, ohne aria-label.
  return (
    <AppLink to={weg.href} className={own.pflegeweg}>
      <span className={own.nurVorgelesen}>{weg.label.slice(0, -' pflegen'.length)} </span>
      <span className={own.grossAnfang}>pflegen</span>
    </AppLink>
  );
}

/**
 * Daten und Verlauf. Im Register "Verlauf" ist dasselbe Panel eingehaengt, nur
 * die Datenkarte ist ausgeblendet und der Verlauf steht allein in voller Breite.
 *
 * Die Datenkarte nach v4: Kontaktwege und Anschriften als Feldlisten, Stichworte,
 * Zugehoerigkeit und Beziehungen offen, jeder Abschnitt mit seinem Pflegeweg in
 * der Ueberschriftenzeile. Zugeklappt bleiben nur das Aendern des Namens (er
 * steht im Kopf) und Kennungen, Herkunft und Datenstand. Zugehoerigkeit bleibt
 * offen, bis der Kopf "Funktion bei Organisation" zeigt (Dirigent 12.10.2026).
 */
function ArbeitsPanel({ kontext, registerId, onRegister }: PanelProps): ReactElement {
  const { party, apiBaseUrl, accessToken, onReauthenticate, accountId, onParty } = kontext;
  const styles = useScreenStyles();
  const own = useKontaktStyles();
  const nurLesen = notWritableNotice(party.lifecycleStatus) !== undefined;
  const aktualisieren = useKontaktAktualisieren(kontext);
  const wege = careLinks(party);
  const organisation = party.kind === 'organization';
  const weg = (seite: string): CareLink | undefined => wege.find((eintrag) => eintrag.label.startsWith(seite));
  // ST-CAM-001 C1a: einmal geladen, im Abschnitt "Sperren" gelistet und an den getroffenen Zeilen gezeigt.
  const sperren = useSperren({ apiBaseUrl, accessToken, onReauthenticate, partyId: party.partyId });
  const gesperrt = sperrenFuer(sperren);
  const { onSperren } = kontext;
  const partyId = party.partyId;
  // Nur an Stand und Daten: das Objekt aus useSperren ist in jedem Rendern neu, der Kopf rendert mit.
  const { status: sperrStatus, data: sperrDaten } = sperren;
  // Kein Layout-Effekt: der synchrone Elternlauf beim Schließen eines Dialogs lässt Tabsters
  // Wurzelplatzhalter tabbar (axe aria-hidden-focus). Der alte Link steht ohnehin bis zum Neuladen.
  useEffect(() => {
    onSperren?.(partyId, sperrenFuer({ status: sperrStatus, data: sperrDaten, laden: async () => undefined }));
  }, [onSperren, partyId, sperrStatus, sperrDaten]);
  // Ohne Panel kennt der Kopf keine Sperrlage mehr und zeigt Text ohne Link.
  useEffect(() => () => onSperren?.(partyId, undefined), [onSperren, partyId]);
  // P7 A: an einer Person, was sie von ihrer Organisation übernimmt; ein Fehler zeigt nichts.
  const ueber = useUeberOrganisation({ apiBaseUrl, accessToken, partyId: party.partyId, aktiv: !organisation, stand: party });
  return (
    <div className={mergeClasses(own.spalten, registerId === 'verlauf' && own.spalteEinzeln)}>
      {/* Wrapper mit hidden: Fluent-Card setzt display und uebersteuert das Attribut. */}
      <div hidden={registerId === 'verlauf'}>
        <Card className={own.flaeche}>
          <Kontaktwege party={party} aktion={<Pflegeweg weg={weg('Kontaktwege')} />} ueber={ueber} gesperrt={gesperrt} />
          {/* Eigener Abruf, eigene Statusregion; gepflegt wird auf der Seite "Anschriften". */}
          <Adressen
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            partyId={party.partyId}
            name={etikettName(party)}
            nurLesen={nurLesen}
            aktion={<Pflegeweg weg={weg('Anschriften')} />}
            ueber={ueber}
            gesperrt={gesperrt}
          />
          {/* Nach den Anschriften: Michaels Reihenfolge Kontaktwege, Anschriften bleibt (Entscheidung in ST-CAM-001). */}
          <Sperren
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            party={party}
            stand={sperren}
            nurLesen={nurLesen}
            onKontaktNeu={aktualisieren}
          />
          <Stichworte
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            partyId={party.partyId}
            nurLesen={nurLesen}
            onNichtBeschreibbar={aktualisieren}
            {...mitAccount(accountId)}
          />
          <Zugehoerigkeit
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            partyId={party.partyId}
            nurLesen={nurLesen}
            onNichtBeschreibbar={aktualisieren}
            {...mitAccount(accountId)}
          />
          {/* key: je Kontakt ein NEUER Abschnitt (Codex-Cloud-Review #282, P2):
              sonst behielte der Abschnitt Lesart, Treffer und gewaehlte
              Gegenpartei des vorigen Kontakts. */}
          <Beziehungen
            key={party.partyId}
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            partyId={party.partyId}
            nurLesen={nurLesen}
            onNichtBeschreibbar={aktualisieren}
            teil={organisation ? 'weitere' : 'alle'}
          />
          <div className={own.gruppe}>
            {/* multiple: beide duerfen zugleich offen sein. Zugeklappte Panels
                haengt Fluent aus (unmountOnExit). */}
            <Accordion
              multiple
              collapsible
              openItems={[...kontext.offen]}
              onToggle={(_, data) => kontext.onOffen(data.openItems.map(String))}
            >
              {/* Die Ueberschrift sagt, was man hier tut (Experience-Review K2). An der
                  Organisation fuehrt derselbe Eintrag zur Namenspflege. */}
              <AccordionItem value="name">
                <Akkordeonkopf id="name">Name ändern</Akkordeonkopf>
                <AccordionPanel>
                  {party.person === undefined ? (
                    weg('Organisationsnamen') === undefined ? undefined : (
                      <AppLink to={weg('Organisationsnamen')?.href ?? ''}>{weg('Organisationsnamen')?.label}</AppLink>
                    )
                  ) : (
                    <div ref={kontext.nameRef}>
                      <NameAbschnitt
                        key={party.partyId}
                        apiBaseUrl={apiBaseUrl}
                        accessToken={accessToken}
                        onReauthenticate={onReauthenticate}
                        party={{ ...party, person: party.person }}
                        onParty={onParty}
                      />
                    </div>
                  )}
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem value="technik">
                <Akkordeonkopf>Kennungen, Herkunft und Datenstand</Akkordeonkopf>
                <AccordionPanel>
                  <div className={styles.detailRow}>
                    <Caption1>Angelegt</Caption1>
                    <Caption1>{formatInstant(party.createdAt)}</Caption1>
                    <Caption1>Zuletzt geändert</Caption1>
                    <Caption1>{formatInstant(party.updatedAt)}</Caption1>
                    <Caption1>Kennung</Caption1>
                    <pre className={styles.detail}>{party.partyId}</pre>
                  </div>
                  {weg('Kennungen') === undefined ? undefined : <AppLink to={weg('Kennungen')?.href ?? ''}>{weg('Kennungen')?.label}</AppLink>}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </div>
        </Card>
      </div>
      {/* An der Organisation stehen die Ansprechpartner:innen mit in der Spalte: sie ist
          dann hoeher als das Fenster (918 px bei 1440 x 900, DESIGN #596) und haftet nicht. */}
      <Card className={mergeClasses(own.flaeche, registerId !== 'verlauf' && !organisation && own.spalteHaftend)}>
        {/* An einer Organisation stehen die Ansprechpartner MIT ihren Aktionen
            ueber dem Verlauf (Slice 6b, DESIGN A′, v4 secAnsprech); links
            bleiben die weiteren Beziehungen, keine Kante steht doppelt. Im
            Register "Verlauf" steht der Verlauf allein. */}
        {organisation ? (
          <div hidden={registerId === 'verlauf'}>
            <Beziehungen
              key={`ansprechpartner-${party.partyId}`}
              apiBaseUrl={apiBaseUrl}
              accessToken={accessToken}
              onReauthenticate={onReauthenticate}
              partyId={party.partyId}
              nurLesen={nurLesen}
              onNichtBeschreibbar={aktualisieren}
              teil="ansprechpartner"
              organisation={{ partyId: party.partyId, displayName: party.displayName }}
            />
          </div>
        ) : undefined}
        <VerlaufInhalt kontext={kontext} registerId={registerId} {...(onRegister === undefined ? {} : { onRegister })} />
      </Card>
    </div>
  );
}

export const KONTAKT_REGISTER: readonly KontaktRegister[] = [
  { id: 'ueberblick', label: 'Überblick', zeigtVerlauf: true, Panel: ArbeitsPanel },
  { id: 'verlauf', label: 'Verlauf', zeigtVerlauf: true, Panel: ArbeitsPanel },
];

/** Die Register, die dieser Kontakt angeboten bekommt. */
export function angebotene(
  party: PartyDetail,
  register: readonly KontaktRegister[] = KONTAKT_REGISTER,
): readonly KontaktRegister[] {
  return register.filter((eintrag) => eintrag.angeboten === undefined || eintrag.angeboten(party));
}

export { panelId, tabId };

function wechsel(
  event: KeyboardEvent<HTMLButtonElement>,
  angebot: readonly KontaktRegister[],
  von: string,
  onWahl: (id: string) => void,
): void {
  // Mit Modifikator gehoeren die Tasten dem Browser (Alt+Pfeil: zurueck, Strg+Pos1/Ende).
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
    return;
  }
  const stelle = angebot.findIndex((eintrag) => eintrag.id === von);
  const ziel =
    event.key === 'ArrowRight'
      ? (stelle + 1) % angebot.length
      : event.key === 'ArrowLeft'
        ? (stelle - 1 + angebot.length) % angebot.length
        : event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? angebot.length - 1
            : undefined;
  const neu = ziel === undefined ? undefined : angebot[ziel];
  if (neu === undefined) {
    return;
  }
  event.preventDefault();
  onWahl(neu.id);
  document.getElementById(tabId(neu.id))?.focus();
}


/**
 * Leiste und Panel. `gewaehlt` kommt vom Bildschirm; ist die Kennung nicht
 * (mehr) im Angebot, gilt das erste Register.
 */
export function Registerleiste({
  kontext,
  gewaehlt,
  onWahl,
  register = KONTAKT_REGISTER,
}: {
  readonly kontext: KontaktKontext;
  readonly gewaehlt: string;
  readonly onWahl: (id: string) => void;
  readonly register?: readonly KontaktRegister[];
}): ReactElement {
  const own = useKontaktStyles();
  // Die Zahl am Register "Verlauf" kommt aus demselben Stand, den der Verlauf
  // dem Kopf meldet: kein eigener Abruf. Die Weitergabe ist stabil, damit der
  // Verlauf seine Meldung nicht bei jedem Rendern wiederholt.
  const [stand, setStand] = useState<FollowUpTally | undefined>(undefined);
  const weitergabe = useRef(kontext.onStand);
  weitergabe.current = kontext.onStand;
  const meldeStand = useCallback((tally: FollowUpTally | undefined) => {
    setStand(tally);
    weitergabe.current(tally);
  }, []);
  const kontextMitStand: KontaktKontext = { ...kontext, onStand: meldeStand };
  const angebot = angebotene(kontext.party, register);
  const aktuell = angebot.find((eintrag) => eintrag.id === gewaehlt) ?? angebot[0];
  if (aktuell === undefined) {
    return <></>;
  }
  // Je verschiedener Panel-Komponente ein Panel; die Register, die es teilen,
  // steuern es alle. Beschriftet wird es vom gewaehlten Register, sonst vom ersten.
  const panels = angebot.reduce<{ Panel: KontaktRegister['Panel']; ids: string[] }[]>((liste, eintrag) => {
    const vorhanden = liste.find((gruppe) => gruppe.Panel === eintrag.Panel);
    if (vorhanden === undefined) {
      liste.push({ Panel: eintrag.Panel, ids: [eintrag.id] });
    } else {
      vorhanden.ids.push(eintrag.id);
    }
    return liste;
  }, []);
  const steuert = (id: string): string => panelId(panels.find((gruppe) => gruppe.ids.includes(id))?.ids[0] ?? id);
  return (
    <>
      {/* overflowX: schmal darf die Leiste in sich scrollen, nie die Seite (WCAG 1.4.10). */}
      <div className={own.leiste}>
        {/* Eigene Leiste statt Fluent TabList: deren Tastaturfuehrung (tabster)
            legt zwei unsichtbare, fokussierbare aria-hidden-Waechter an, die
            axe als aria-hidden-focus meldet und die jede axe-Probe der
            Kontaktseite rot faerbten. Verhalten wie im ARIA-Muster: ein
            Tabstopp (das gewaehlte Register), Pfeile, Home und Ende wechseln und
            waehlen zugleich. */}
        <div role="tablist" aria-label="Bereiche des Kontakts" className={own.tabs}>
          {angebot.map((eintrag) => {
            const gewaehltEintrag = eintrag.id === aktuell.id;
            return (
              <button
                key={eintrag.id}
                type="button"
                role="tab"
                id={tabId(eintrag.id)}
                aria-selected={gewaehltEintrag}
                aria-controls={steuert(eintrag.id)}
                tabIndex={gewaehltEintrag ? 0 : -1}
                className={mergeClasses(own.tab, gewaehltEintrag && own.tabGewaehlt)}
                onClick={() => onWahl(eintrag.id)}
                onKeyDown={(event) => wechsel(event, angebot, eintrag.id, onWahl)}
              >
                {eintrag.id === 'verlauf' ? verlaufBezeichnung(stand) : eintrag.label}
              </button>
            );
          })}
        </div>
      </div>
      {panels.map(({ Panel, ids }) => {
        const aktiv = ids.includes(aktuell.id);
        return (
          <div
            key={ids[0]}
            role="tabpanel"
            id={panelId(ids[0] ?? '')}
            aria-labelledby={tabId(aktiv ? aktuell.id : (ids[0] ?? ''))}
            hidden={!aktiv}
            className={own.panel}
          >
            <Panel kontext={kontextMitStand} registerId={aktuell.id} onRegister={onWahl} />
          </div>
        );
      })}
    </>
  );
}

/** Die Person auf dem Etikett: Vor- und Nachname ohne Anrede und Titel, sonst der Anzeigename. */
function etikettName(party: PartyDetail): string {
  const teile = [party.person?.givenName, party.person?.familyName].filter((teil): teil is string => teil !== undefined && teil.trim() !== '');
  return teile.length > 0 ? teile.join(' ') : party.displayName;
}
